import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, timer } from 'rxjs';
import { catchError, debounceTime, finalize, map, shareReplay } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { userHasMenu } from '../../auth/menu-access';
import { ConfigService } from '../../config.service';

// ============================================================================
// Csapatchat: fő chat + privát beszélgetések, élő eseményfolyam (SSE).
// ============================================================================

export const MENU_CHAT = 'Chat';

export type ChatRefType = 'order' | 'ticket' | 'mail' | 'po';

export interface ChatMember {
  id: number;
  name: string;
  image: string | null;
  online: boolean;
  lastSeenAt: string | null;
  /** Foxi, az MI-munkatárs */
  bot?: boolean;
}

export interface ChatRefCard {
  type: ChatRefType;
  id: number;
  title: string;
  subtitle: string;
  meta: string;
  status: string;
  link: string;
  restricted: boolean;
  missing: boolean;
  /** gyors művelet a kártyán (pl. beszerzési rendelés jóváhagyása) — a jogot a szerver ellenőrzi */
  action?: { kind: 'approve_po' | 'robot_go'; label: string } | null;
}

export interface ChatAttachment {
  id: number;
  name: string;
  mime: string;
  size: number;
  isImage: boolean;
  hasThumb: boolean;
  width: number | null;
  height: number | null;
}

export interface ChatMessage {
  id: number;
  conversationId: number;
  authorId: number | null;
  text: string;
  createdAt: string;
  editedAt: string | null;
  deleted: boolean;
  replyTo: { id: number; authorId: number | null; text: string; deleted: boolean; attachments: number; refs: number } | null;
  attachments: ChatAttachment[];
  refs: ChatRefCard[];
  reactions: { emoji: string; userIds: number[] }[];
}

export interface ChatReadState {
  userId: number;
  lastReadMessageId: number;
  lastReadAt: string | null;
}

export interface ChatConversation {
  id: number;
  type: 'main' | 'direct';
  peerId: number | null;
  lastMessage: { id: number; authorId: number | null; preview: string; createdAt: string } | null;
  unread: number;
  muted: boolean;
}

export interface ChatOverview {
  me: number;
  members: ChatMember[];
  conversations: ChatConversation[];
  reactions: string[];
}

export interface ChatPage {
  messages: ChatMessage[];
  hasMore: boolean;
  reads: ChatReadState[];
}

/** Az élő folyam eseményei. */
export type ChatStreamEvent =
  | { type: 'message' | 'message_update'; data: ChatMessage }
  | { type: 'read'; data: ChatReadState & { conversationId: number } }
  | { type: 'typing'; data: { conversationId: number; userId: number } }
  | { type: 'presence'; data: { userId: number; online: boolean; lastSeenAt?: string } }
  | { type: 'reconnected'; data: null };

const STREAM_EVENTS = ['message', 'message_update', 'read', 'typing', 'presence'] as const;

@Injectable({ providedIn: 'root' })
export class ChatService {
  /** olvasatlan üzenetek összesen (a némított beszélgetések nélkül) — menü / alsó sáv jelvénye */
  readonly unread$ = new BehaviorSubject<number>(0);
  readonly events$ = new Subject<ChatStreamEvent>();
  readonly online$ = new BehaviorSubject<Set<number>>(new Set());
  readonly connected$ = new BehaviorSubject<boolean>(false);
  readonly reactions = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

  private source: EventSource | null = null;
  private connectionId = 0;
  private started = false;
  private everConnected = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private focusConversationId: number | null = null;
  private readonly unreadRefresh$ = new Subject<void>();
  private readonly blobCache = new Map<string, Observable<string>>();

  constructor(
    private http: HttpClient,
    private auth: AuthService,
    private config: ConfigService,
    private zone: NgZone,
  ) {}

  get enabled(): boolean {
    return userHasMenu(this.auth.user, MENU_CHAT);
  }

  /** Indítás bejelentkezés után: élő kapcsolat + számláló. Kijelentkezéskor a kapcsolat bezárul. */
  start(): void {
    if (!this.enabled) {
      return;
    }
    if (!this.started) {
      this.started = true;
      this.unreadRefresh$.pipe(debounceTime(400)).subscribe(() => this.refreshUnread());
      // tartalék: ha az élő kapcsolat valahol elakadna, a számláló akkor is frissül
      timer(90 * 1000, 90 * 1000).subscribe(() => this.refreshUnread());
      document.addEventListener('visibilitychange', () => {
        if (!this.auth.jwt) {
          return;
        }
        if (document.visibilityState === 'visible') {
          this.refreshUnread();
          if (!this.source || this.source.readyState === EventSource.CLOSED) {
            this.connect();
          }
        }
        this.sendFocus();
      });
      this.auth.isAuthenticatedSubject.subscribe((ok) => {
        if (!ok) {
          this.stop();
        }
      });
    }
    this.connect();
    this.refreshUnread();
  }

  private stop(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.source?.close();
    this.source = null;
    this.connectionId = 0;
    this.everConnected = false;
    this.unread$.next(0);
    this.connected$.next(false);
    this.blobCache.clear();
  }

  private connect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.source?.close();
    if (!this.auth.jwt || !this.enabled) {
      return;
    }
    // ngsw-bypass: a service worker ne nyúljon a folyamhoz
    const url = `${this.config.apiUrl}/chat/stream?token=${encodeURIComponent(this.auth.jwt)}&ngsw-bypass=1`;
    // Az EventSource a zónán kívül fut: a ping-ek ne indítsanak felesleges változásfigyelést.
    this.zone.runOutsideAngular(() => {
      const source = new EventSource(url);
      this.source = source;
      source.addEventListener('hello', (e) => {
        const data = JSON.parse((e as MessageEvent).data);
        this.zone.run(() => {
          this.connectionId = data.connectionId;
          this.online$.next(new Set<number>(data.online || []));
          this.connected$.next(true);
          if (this.everConnected) {
            // újrakapcsolódás: a kimaradt üzeneteket a nyitott nézet újratölti
            this.events$.next({ type: 'reconnected', data: null });
            this.refreshUnread();
          }
          this.everConnected = true;
          this.sendFocus();
        });
      });
      STREAM_EVENTS.forEach((type) =>
        source.addEventListener(type, (e) => {
          const data = JSON.parse((e as MessageEvent).data);
          this.zone.run(() => this.handle({ type, data } as ChatStreamEvent));
        }),
      );
      source.onerror = () => {
        this.zone.run(() => this.connected$.next(false));
        // A böngésző magától újrapróbál; ha végleg lezárta (pl. 401), mi próbáljuk újra később.
        if (source.readyState === EventSource.CLOSED && this.source === source) {
          this.reconnectTimer = setTimeout(() => this.connect(), 8000);
        }
      };
    });
  }

  private handle(event: ChatStreamEvent): void {
    if (event.type === 'presence') {
      const next = new Set(this.online$.value);
      if (event.data.online) {
        next.add(event.data.userId);
      } else {
        next.delete(event.data.userId);
      }
      this.online$.next(next);
    }
    if (event.type === 'message' || (event.type === 'read' && event.data.userId === this.auth.user?.id)) {
      this.unreadRefresh$.next();
    }
    this.events$.next(event);
  }

  refreshUnread(): void {
    if (!this.enabled || !this.auth.jwt) {
      return;
    }
    this.http.get<{ unread: number }>('chat/unread-count')
      .pipe(catchError(() => of({ unread: this.unread$.value })))
      .subscribe((result) => this.unread$.next(result?.unread || 0));
  }

  /** Melyik beszélgetés van nyitva ezen az eszközön (oda nem jön push). */
  setFocus(conversationId: number | null): void {
    this.focusConversationId = conversationId;
    this.sendFocus();
  }

  private sendFocus(): void {
    if (!this.connectionId) {
      return;
    }
    this.http.post('chat/focus', {
      connectionId: this.connectionId,
      conversationId: this.focusConversationId,
      visible: document.visibilityState === 'visible',
    }).subscribe({ error: () => undefined });
  }

  // ------------------------------------------------------------------ API

  overview(): Observable<ChatOverview> {
    return this.http.get<ChatOverview>('chat/overview');
  }

  direct(userId: number): Observable<ChatConversation> {
    return this.http.post<ChatConversation>(`chat/direct/${userId}`, {});
  }

  messages(conversationId: number, before?: number): Observable<ChatPage> {
    return this.http.get<ChatPage>(`chat/conversations/${conversationId}/messages${before ? `?before=${before}` : ''}`);
  }

  /** Küldés feltöltés-állapottal (a fájloknál a folyamatjelzőhöz). */
  send(conversationId: number, input: { text: string; replyToId?: number | null; refs?: { type: ChatRefType; id: number }[]; files?: File[] }): Observable<HttpEvent<ChatMessage>> {
    const form = new FormData();
    form.append('text', input.text || '');
    if (input.replyToId) {
      form.append('replyToId', `${input.replyToId}`);
    }
    form.append('refs', JSON.stringify(input.refs || []));
    (input.files || []).forEach((file) => form.append('files', file, file.name));
    return this.http.post<ChatMessage>(`chat/conversations/${conversationId}/messages`, form, { reportProgress: true, observe: 'events' });
  }

  edit(messageId: number, text: string): Observable<ChatMessage> {
    return this.http.patch<ChatMessage>(`chat/messages/${messageId}`, { text });
  }

  remove(messageId: number): Observable<ChatMessage> {
    return this.http.delete<ChatMessage>(`chat/messages/${messageId}`);
  }

  react(messageId: number, emoji: string | null): Observable<ChatMessage> {
    return this.http.put<ChatMessage>(`chat/messages/${messageId}/reaction`, { emoji });
  }

  seenBy(messageId: number): Observable<{ userId: number; seenAt: string | null }[]> {
    return this.http.get<{ userId: number; seenAt: string | null }[]>(`chat/messages/${messageId}/seen`);
  }

  markRead(conversationId: number, messageId: number): Observable<ChatReadState> {
    return this.http.post<ChatReadState>(`chat/conversations/${conversationId}/read`, { messageId });
  }

  typing(conversationId: number): void {
    this.http.post(`chat/conversations/${conversationId}/typing`, {}).subscribe({ error: () => undefined });
  }

  setMuted(conversationId: number, muted: boolean): Observable<{ muted: boolean }> {
    return this.http.patch<{ muted: boolean }>(`chat/conversations/${conversationId}`, { muted }).pipe(
      finalize(() => this.refreshUnread()),
    );
  }

  searchRefs(type: ChatRefType, q: string, id?: number): Observable<ChatRefCard[]> {
    return this.http.get<ChatRefCard[]>(`chat/refs?type=${type}&q=${encodeURIComponent(q || '')}${id ? `&id=${id}` : ''}`);
  }

  // --------------------------------------------------------------- képek

  /**
   * Auth-fejléces letöltés blob-URL-ként, gyorsítótárazva (profilképek, csatolmányok):
   * ugyanaz a kép egy oldalon sokszor szerepel, de csak egyszer töltődik le.
   */
  blobUrl(path: string | null | undefined): Observable<string | null> {
    if (!path) {
      return of(null);
    }
    let cached = this.blobCache.get(path);
    if (!cached) {
      cached = this.http.get(path, { responseType: 'blob' }).pipe(
        map((blob) => URL.createObjectURL(blob)),
        catchError(() => {
          // a hiba is gyorsítótárba kerül (különben minden újrarajzolás új kérést indítana);
          // egy perc múlva újra lehet próbálni
          setTimeout(() => this.blobCache.delete(path), 60 * 1000);
          return of('');
        }),
        shareReplay(1),
      );
      this.blobCache.set(path, cached);
    }
    return cached;
  }

  attachmentPath(attachment: ChatAttachment, thumb = false): string {
    return `chat/attachments/${attachment.id}${thumb ? '?thumb=1' : ''}`;
  }

  /** Letöltés az eredeti fájlnévvel. */
  download(attachment: ChatAttachment): void {
    this.http.get(this.attachmentPath(attachment), { responseType: 'blob' }).subscribe((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    });
  }
}
